import type { ContentHash, ContentRef } from "@cogo/publisher-core";

// Browser-safe hashing — see application-profile.ts.
import { hashCanonicalContent } from "./canonical-content-hash";

import type { AcceptanceInventoryKind } from "./application-acceptance-manifest";
import type { ApplicationOntologySourceSelector } from "./application-ontology-package";

export interface TopologySourceProvenance {
  provenanceId: string;
  inventoryRow: { kind: AcceptanceInventoryKind; id: string };
  sourceLocatorId: string;
  sourceRef: ContentRef;
  selector: ApplicationOntologySourceSelector;
  selectedBytesHash: ContentHash;
}

interface TopologyRowBase {
  sourceProvenanceIds: readonly string[];
}

export interface RequiredApplicationTopologySlice {
  schemaVersion: "cogo.required-application-topology-slice.v1";
  applicationOntologyPackageRef: ContentRef;
  manifestRef: ContentRef;
  roles: readonly (TopologyRowBase & {
    roleId: string;
    permissionIds: readonly string[];
    policyIds: readonly string[];
  })[];
  routes: readonly (TopologyRowBase & {
    routeId: string;
    path: string;
    screenIds: readonly string[];
  })[];
  screens: readonly (TopologyRowBase & {
    screenId: string;
    routeId: string;
    eventIds: readonly string[];
  })[];
  journeys: readonly (TopologyRowBase & {
    journeyId: string;
    entryNodeId: string;
    edgeIds: readonly string[];
    terminalOutcomeIds: readonly string[];
  })[];
  branches: readonly (TopologyRowBase & {
    branchId: string;
    conditionId: string;
    positiveEdgeId: string;
    negativeEdgeId: string;
  })[];
  events: readonly (TopologyRowBase & {
    eventId: string;
    screenId: string;
    actionCatalogId: `action:${string}`;
    transitionId?: string;
  })[];
  journeyEdges: readonly (TopologyRowBase & {
    edgeId: string;
    fromNodeId: string;
    toNodeId: string;
    eventId?: string;
    branchId?: string;
  })[];
  errorPaths: readonly (TopologyRowBase & {
    errorPathId: string;
    fromEventId: string;
    errorCode: string;
    recoveryPathIds: readonly string[];
  })[];
  recoveryPaths: readonly (TopologyRowBase & {
    recoveryPathId: string;
    fromErrorPathId: string;
    actionCatalogIds: readonly `action:${string}`[];
    toNodeId: string;
  })[];
  permissions: readonly (TopologyRowBase & {
    permissionId: string;
    roleIds: readonly string[];
    policyIds: readonly string[];
  })[];
  policies: readonly (TopologyRowBase & {
    policyId: string;
    kind: "auth" | "consent" | "business" | "publication";
    contractRef?: ContentRef;
  })[];
  terminalOutcomeEdges: readonly (TopologyRowBase & {
    edgeId: string;
    fromNodeId: string;
    outcomeCatalogId: `terminal-outcome:${string}`;
    conditionId?: string;
  })[];
  sourceProvenance: readonly TopologySourceProvenance[];
  topologyHash: ContentHash;
}

const HASH = /^sha256:[a-f0-9]{64}$/;
const ACTION_ID = /^action:[^:]+$/;
const OUTCOME_ID = /^terminal-outcome:[^:]+$/;
const INVENTORY_KINDS = new Set<AcceptanceInventoryKind>([
  "role", "route", "surface", "journey", "branch", "event", "permission",
  "policy", "action", "state", "datasource", "error-path", "recovery-path",
  "terminal-outcome",
]);

function compare(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function assertPlainObject(
  value: unknown,
  label: string,
): asserts value is Record<string, unknown> {
  if (
    typeof value !== "object" ||
    value === null ||
    Array.isArray(value) ||
    Object.getPrototypeOf(value) !== Object.prototype
  ) {
    throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_INVALID_${label}`);
  }
}

function assertExactKeys(
  value: unknown,
  required: readonly string[],
  optional: readonly string[],
  label: string,
): asserts value is Record<string, unknown> {
  assertPlainObject(value, label);
  const allowed = new Set([...required, ...optional]);
  const actual = Object.keys(value);
  if (
    actual.some((key) => !allowed.has(key)) ||
    required.some(
      (key) => !Object.prototype.hasOwnProperty.call(value, key),
    )
  ) {
    throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_INVALID_${label}`);
  }
}

function assertArray(value: unknown): asserts value is readonly unknown[] {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) {
    throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_ARRAY");
  }
}

function assertText(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_INVALID_${label}`);
  }
}

function assertContentHash(value: unknown, label: string): asserts value is ContentHash {
  if (typeof value !== "string" || !HASH.test(value)) {
    throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_INVALID_${label}`);
  }
}

function assertContentRef(value: unknown, label: string): asserts value is ContentRef {
  assertExactKeys(value, ["ref", "contentHash"], [], "CONTENT_REF");
  assertText(value.ref, `CONTENT_REF:${label}`);
  assertContentHash(value.contentHash, `CONTENT_REF_HASH:${label}`);
}

function assertUniqueTextArray(
  value: unknown,
  label: string,
  minItems = 0,
): asserts value is readonly string[] {
  assertArray(value);
  if (value.length < minItems) {
    throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_EMPTY_${label}`);
  }
  const seen = new Set<string>();
  for (const entry of value) {
    assertText(entry, label);
    if (seen.has(entry)) {
      throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_DUPLICATE_${label}`);
    }
    seen.add(entry);
  }
}

function assertSourceSelector(value: unknown): asserts value is ApplicationOntologySourceSelector {
  assertPlainObject(value, "SOURCE_SELECTOR");
  if (value.kind === "line-range") {
    assertExactKeys(
      value,
      ["kind", "startLine", "endLine"],
      [],
      "SOURCE_SELECTOR",
    );
    if (
      !Number.isInteger(value.startLine) ||
      !Number.isInteger(value.endLine) ||
      (value.startLine as number) < 1 ||
      (value.endLine as number) < (value.startLine as number)
    ) {
      throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_SOURCE_SELECTOR");
    }
    return;
  }
  if (value.kind === "json-pointer") {
    assertExactKeys(value, ["kind", "pointer"], [], "SOURCE_SELECTOR");
    if (typeof value.pointer !== "string" || !value.pointer.startsWith("/")) {
      throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_SOURCE_SELECTOR");
    }
    return;
  }
  if (value.kind === "rdf-subject") {
    assertExactKeys(value, ["kind", "subject"], [], "SOURCE_SELECTOR");
    assertText(value.subject, "SOURCE_SELECTOR");
    return;
  }
  throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_SOURCE_SELECTOR");
}

function assertUniqueId(
  seen: Set<string>,
  value: unknown,
  label: string,
): asserts value is string {
  assertText(value, label);
  if (seen.has(value)) {
    throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_DUPLICATE_${label}`);
  }
  seen.add(value);
}

function assertResolved(
  values: ReadonlySet<string>,
  value: string,
  label: string,
): void {
  if (!values.has(value)) {
    throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_UNRESOLVED_${label}:${value}`);
  }
}

function sortedText(values: readonly string[]): string[] {
  return [...values].sort(compare);
}

function canonicalContentRef(value: ContentRef): ContentRef {
  return { ref: value.ref, contentHash: value.contentHash };
}

function canonicalSelector(
  value: ApplicationOntologySourceSelector,
): ApplicationOntologySourceSelector {
  return { ...value };
}

function canonicalRows<T extends TopologyRowBase>(
  values: readonly T[],
  id: (value: T) => string,
  stringArrays: readonly (keyof T)[],
): T[] {
  return values
    .map((value) => {
      const copy = { ...value } as unknown as Record<string, unknown>;
      copy.sourceProvenanceIds = sortedText(value.sourceProvenanceIds);
      for (const key of stringArrays) {
        copy[key as string] = sortedText(value[key] as readonly string[]);
      }
      if (copy.contractRef) {
        copy.contractRef = canonicalContentRef(copy.contractRef as ContentRef);
      }
      return copy as T;
    })
    .sort((left, right) => compare(id(left), id(right)));
}

function canonicalWithoutHash(
  value: Omit<RequiredApplicationTopologySlice, "topologyHash">,
): Omit<RequiredApplicationTopologySlice, "topologyHash"> {
  return {
    schemaVersion: value.schemaVersion,
    applicationOntologyPackageRef: canonicalContentRef(
      value.applicationOntologyPackageRef,
    ),
    manifestRef: canonicalContentRef(value.manifestRef),
    roles: canonicalRows(value.roles, (row) => row.roleId, [
      "permissionIds",
      "policyIds",
    ]),
    routes: canonicalRows(value.routes, (row) => row.routeId, ["screenIds"]),
    screens: canonicalRows(value.screens, (row) => row.screenId, ["eventIds"]),
    journeys: canonicalRows(value.journeys, (row) => row.journeyId, [
      "edgeIds",
      "terminalOutcomeIds",
    ]),
    branches: canonicalRows(value.branches, (row) => row.branchId, []),
    events: canonicalRows(value.events, (row) => row.eventId, []),
    journeyEdges: canonicalRows(value.journeyEdges, (row) => row.edgeId, []),
    errorPaths: canonicalRows(value.errorPaths, (row) => row.errorPathId, [
      "recoveryPathIds",
    ]),
    recoveryPaths: canonicalRows(
      value.recoveryPaths,
      (row) => row.recoveryPathId,
      ["actionCatalogIds"],
    ),
    permissions: canonicalRows(
      value.permissions,
      (row) => row.permissionId,
      ["roleIds", "policyIds"],
    ),
    policies: canonicalRows(value.policies, (row) => row.policyId, []),
    terminalOutcomeEdges: canonicalRows(
      value.terminalOutcomeEdges,
      (row) => row.edgeId,
      [],
    ),
    sourceProvenance: value.sourceProvenance
      .map((row) => ({
        ...row,
        inventoryRow: { ...row.inventoryRow },
        sourceRef: canonicalContentRef(row.sourceRef),
        selector: canonicalSelector(row.selector),
      }))
      .sort((left, right) => compare(left.provenanceId, right.provenanceId)),
  };
}

function validateRowSources(
  rows: readonly TopologyRowBase[],
  provenanceIds: ReadonlySet<string>,
): void {
  for (const row of rows) {
    assertUniqueTextArray(
      row.sourceProvenanceIds,
      "SOURCE_PROVENANCE_REF",
      1,
    );
    for (const provenanceId of row.sourceProvenanceIds) {
      assertResolved(provenanceIds, provenanceId, "SOURCE_PROVENANCE");
    }
  }
}

export function assertRequiredApplicationTopologySliceIntrinsicIntegrity(
  value: RequiredApplicationTopologySlice,
): void {
  assertExactKeys(
    value,
    [
      "schemaVersion", "applicationOntologyPackageRef", "manifestRef", "roles",
      "routes", "screens", "journeys", "branches", "events", "journeyEdges",
      "errorPaths", "recoveryPaths", "permissions", "policies",
      "terminalOutcomeEdges", "sourceProvenance", "topologyHash",
    ],
    [],
    "DOCUMENT",
  );
  if (value.schemaVersion !== "cogo.required-application-topology-slice.v1") {
    throw new Error("REQUIRED_APPLICATION_TOPOLOGY_SCHEMA_VERSION_MISMATCH");
  }
  assertContentRef(value.applicationOntologyPackageRef, "APPLICATION_ONTOLOGY_PACKAGE");
  assertContentRef(value.manifestRef, "MANIFEST");
  assertContentHash(value.topologyHash, "TOPOLOGY_HASH");

  for (const rows of [
    value.roles, value.routes, value.screens, value.journeys, value.branches,
    value.events, value.journeyEdges, value.errorPaths, value.recoveryPaths,
    value.permissions, value.policies, value.terminalOutcomeEdges,
    value.sourceProvenance,
  ]) assertArray(rows);

  const provenanceIds = new Set<string>();
  for (const row of value.sourceProvenance) {
    assertExactKeys(
      row,
      [
        "provenanceId", "inventoryRow", "sourceLocatorId", "sourceRef",
        "selector", "selectedBytesHash",
      ],
      [],
      "SOURCE_PROVENANCE",
    );
    assertUniqueId(provenanceIds, row.provenanceId, "SOURCE_PROVENANCE_ID");
    assertExactKeys(row.inventoryRow, ["kind", "id"], [], "INVENTORY_ROW");
    if (!INVENTORY_KINDS.has(row.inventoryRow.kind as AcceptanceInventoryKind)) {
      throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_INVENTORY_KIND");
    }
    assertText(row.inventoryRow.id, "INVENTORY_ROW_ID");
    assertText(row.sourceLocatorId, "SOURCE_LOCATOR_ID");
    assertContentRef(row.sourceRef, "SOURCE_PROVENANCE");
    assertSourceSelector(row.selector);
    if (typeof row.selectedBytesHash !== "string" || !HASH.test(row.selectedBytesHash)) {
      throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_SELECTED_BYTES_HASH");
    }
  }

  const roleIds = new Set<string>();
  const routeIds = new Set<string>();
  const screenIds = new Set<string>();
  const journeyIds = new Set<string>();
  const branchIds = new Set<string>();
  const eventIds = new Set<string>();
  const journeyEdgeIds = new Set<string>();
  const errorPathIds = new Set<string>();
  const recoveryPathIds = new Set<string>();
  const permissionIds = new Set<string>();
  const policyIds = new Set<string>();
  const terminalEdgeIds = new Set<string>();
  const outcomeIds = new Set<string>();

  for (const row of value.roles) {
    assertExactKeys(row, ["roleId", "permissionIds", "policyIds", "sourceProvenanceIds"], [], "ROLE");
    assertUniqueId(roleIds, row.roleId, "ROLE_ID");
    assertUniqueTextArray(row.permissionIds, "PERMISSION_REF");
    assertUniqueTextArray(row.policyIds, "POLICY_REF");
  }
  for (const row of value.routes) {
    assertExactKeys(row, ["routeId", "path", "screenIds", "sourceProvenanceIds"], [], "ROUTE");
    assertUniqueId(routeIds, row.routeId, "ROUTE_ID");
    assertText(row.path, "ROUTE_PATH");
    assertUniqueTextArray(row.screenIds, "SCREEN_REF");
  }
  for (const row of value.screens) {
    assertExactKeys(row, ["screenId", "routeId", "eventIds", "sourceProvenanceIds"], [], "SCREEN");
    assertUniqueId(screenIds, row.screenId, "SCREEN_ID");
    assertText(row.routeId, "ROUTE_REF");
    assertUniqueTextArray(row.eventIds, "EVENT_REF");
  }
  for (const row of value.journeys) {
    assertExactKeys(row, ["journeyId", "entryNodeId", "edgeIds", "terminalOutcomeIds", "sourceProvenanceIds"], [], "JOURNEY");
    assertUniqueId(journeyIds, row.journeyId, "JOURNEY_ID");
    assertText(row.entryNodeId, "NODE_REF");
    assertUniqueTextArray(row.edgeIds, "EDGE_REF");
    assertUniqueTextArray(row.terminalOutcomeIds, "OUTCOME_REF");
  }
  for (const row of value.branches) {
    assertExactKeys(row, ["branchId", "conditionId", "positiveEdgeId", "negativeEdgeId", "sourceProvenanceIds"], [], "BRANCH");
    assertUniqueId(branchIds, row.branchId, "BRANCH_ID");
    assertText(row.conditionId, "CONDITION_ID");
    assertText(row.positiveEdgeId, "EDGE_REF");
    assertText(row.negativeEdgeId, "EDGE_REF");
    if (row.positiveEdgeId === row.negativeEdgeId) {
      throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_BRANCH_LEAVES_REQUIRED:${row.branchId}`);
    }
  }
  for (const row of value.events) {
    assertExactKeys(row, ["eventId", "screenId", "actionCatalogId", "sourceProvenanceIds"], ["transitionId"], "EVENT");
    assertUniqueId(eventIds, row.eventId, "EVENT_ID");
    assertText(row.screenId, "SCREEN_REF");
    if (!ACTION_ID.test(row.actionCatalogId)) throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_ACTION_CATALOG_ID");
    if (row.transitionId !== undefined) assertText(row.transitionId, "TRANSITION_ID");
  }
  for (const row of value.journeyEdges) {
    assertExactKeys(row, ["edgeId", "fromNodeId", "toNodeId", "sourceProvenanceIds"], ["eventId", "branchId"], "JOURNEY_EDGE");
    assertUniqueId(journeyEdgeIds, row.edgeId, "JOURNEY_EDGE_ID");
    assertText(row.fromNodeId, "NODE_REF");
    assertText(row.toNodeId, "NODE_REF");
    if (row.eventId !== undefined) assertText(row.eventId, "EVENT_REF");
    if (row.branchId !== undefined) assertText(row.branchId, "BRANCH_REF");
  }
  for (const row of value.errorPaths) {
    assertExactKeys(row, ["errorPathId", "fromEventId", "errorCode", "recoveryPathIds", "sourceProvenanceIds"], [], "ERROR_PATH");
    assertUniqueId(errorPathIds, row.errorPathId, "ERROR_PATH_ID");
    assertText(row.fromEventId, "EVENT_REF");
    assertText(row.errorCode, "ERROR_CODE");
    assertUniqueTextArray(row.recoveryPathIds, "RECOVERY_PATH_REF");
  }
  for (const row of value.recoveryPaths) {
    assertExactKeys(row, ["recoveryPathId", "fromErrorPathId", "actionCatalogIds", "toNodeId", "sourceProvenanceIds"], [], "RECOVERY_PATH");
    assertUniqueId(recoveryPathIds, row.recoveryPathId, "RECOVERY_PATH_ID");
    assertText(row.fromErrorPathId, "ERROR_PATH_REF");
    assertUniqueTextArray(row.actionCatalogIds, "ACTION_CATALOG_REF", 1);
    if (row.actionCatalogIds.some((id) => !ACTION_ID.test(id))) throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_ACTION_CATALOG_ID");
    assertText(row.toNodeId, "NODE_REF");
  }
  for (const row of value.permissions) {
    assertExactKeys(row, ["permissionId", "roleIds", "policyIds", "sourceProvenanceIds"], [], "PERMISSION");
    assertUniqueId(permissionIds, row.permissionId, "PERMISSION_ID");
    assertUniqueTextArray(row.roleIds, "ROLE_REF", 1);
    assertUniqueTextArray(row.policyIds, "POLICY_REF");
  }
  for (const row of value.policies) {
    assertExactKeys(row, ["policyId", "kind", "sourceProvenanceIds"], ["contractRef"], "POLICY");
    assertUniqueId(policyIds, row.policyId, "POLICY_ID");
    if (!["auth", "consent", "business", "publication"].includes(row.kind)) throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_POLICY_KIND");
    if (row.contractRef !== undefined) assertContentRef(row.contractRef, "POLICY_CONTRACT");
  }
  for (const row of value.terminalOutcomeEdges) {
    assertExactKeys(row, ["edgeId", "fromNodeId", "outcomeCatalogId", "sourceProvenanceIds"], ["conditionId"], "TERMINAL_OUTCOME_EDGE");
    assertUniqueId(terminalEdgeIds, row.edgeId, "TERMINAL_OUTCOME_EDGE_ID");
    if (journeyEdgeIds.has(row.edgeId)) throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_DUPLICATE_EDGE_ID:${row.edgeId}`);
    assertText(row.fromNodeId, "NODE_REF");
    if (!OUTCOME_ID.test(row.outcomeCatalogId)) throw new Error("REQUIRED_APPLICATION_TOPOLOGY_INVALID_OUTCOME_CATALOG_ID");
    assertUniqueId(outcomeIds, row.outcomeCatalogId, "OUTCOME_ID");
    if (row.conditionId !== undefined) assertText(row.conditionId, "CONDITION_ID");
  }

  const nodeIds = new Set<string>();
  for (const ids of [
    routeIds,
    screenIds,
    journeyIds,
    branchIds,
    eventIds,
    errorPathIds,
    recoveryPathIds,
  ]) {
    for (const id of ids) {
      if (nodeIds.has(id)) {
        throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_AMBIGUOUS_NODE_ID:${id}`);
      }
      nodeIds.add(id);
    }
  }

  validateRowSources([
    ...value.roles, ...value.routes, ...value.screens, ...value.journeys,
    ...value.branches, ...value.events, ...value.journeyEdges,
    ...value.errorPaths, ...value.recoveryPaths, ...value.permissions,
    ...value.policies, ...value.terminalOutcomeEdges,
  ], provenanceIds);

  for (const row of value.routes) {
    for (const id of row.screenIds) assertResolved(screenIds, id, "SCREEN");
  }
  for (const row of value.screens) {
    assertResolved(routeIds, row.routeId, "ROUTE");
    const route = value.routes.find((candidate) => candidate.routeId === row.routeId)!;
    if (!route.screenIds.includes(row.screenId)) throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_UNRESOLVED_ROUTE_SCREEN:${row.screenId}`);
    for (const id of row.eventIds) assertResolved(eventIds, id, "EVENT");
  }
  for (const row of value.events) {
    assertResolved(screenIds, row.screenId, "SCREEN");
    const screen = value.screens.find((candidate) => candidate.screenId === row.screenId)!;
    if (!screen.eventIds.includes(row.eventId)) throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_UNRESOLVED_SCREEN_EVENT:${row.eventId}`);
  }
  for (const row of value.journeys) {
    assertResolved(nodeIds, row.entryNodeId, "JOURNEY_ENTRY_NODE");
    for (const id of row.edgeIds) assertResolved(journeyEdgeIds, id, "JOURNEY_EDGE");
    for (const id of row.terminalOutcomeIds) assertResolved(outcomeIds, id, "TERMINAL_OUTCOME");
  }
  for (const row of value.branches) {
    assertResolved(journeyEdgeIds, row.positiveEdgeId, "BRANCH_POSITIVE_EDGE");
    assertResolved(journeyEdgeIds, row.negativeEdgeId, "BRANCH_NEGATIVE_EDGE");
    const positiveEdge = value.journeyEdges.find(
      (candidate) => candidate.edgeId === row.positiveEdgeId,
    )!;
    const negativeEdge = value.journeyEdges.find(
      (candidate) => candidate.edgeId === row.negativeEdgeId,
    )!;
    if (
      positiveEdge.branchId !== row.branchId ||
      positiveEdge.fromNodeId !== row.branchId ||
      negativeEdge.branchId !== row.branchId ||
      negativeEdge.fromNodeId !== row.branchId
    ) {
      throw new Error(
        `REQUIRED_APPLICATION_TOPOLOGY_BRANCH_LEAF_MISMATCH:${row.branchId}`,
      );
    }
  }
  for (const row of value.journeyEdges) {
    assertResolved(nodeIds, row.fromNodeId, "EDGE_FROM_NODE");
    assertResolved(nodeIds, row.toNodeId, "EDGE_TO_NODE");
    if (row.eventId !== undefined) assertResolved(eventIds, row.eventId, "EDGE_EVENT");
    if (row.branchId !== undefined) {
      assertResolved(branchIds, row.branchId, "EDGE_BRANCH");
      const branch = value.branches.find(
        (candidate) => candidate.branchId === row.branchId,
      )!;
      if (
        row.edgeId !== branch.positiveEdgeId &&
        row.edgeId !== branch.negativeEdgeId
      ) {
        throw new Error(
          `REQUIRED_APPLICATION_TOPOLOGY_BRANCH_EDGE_NOT_LEAF:${row.edgeId}`,
        );
      }
    }
  }
  for (const row of value.errorPaths) {
    assertResolved(eventIds, row.fromEventId, "ERROR_EVENT");
    for (const id of row.recoveryPathIds) assertResolved(recoveryPathIds, id, "ERROR_RECOVERY");
  }
  for (const row of value.recoveryPaths) {
    assertResolved(errorPathIds, row.fromErrorPathId, "RECOVERY_ERROR");
    assertResolved(nodeIds, row.toNodeId, "RECOVERY_NODE");
    const error = value.errorPaths.find((candidate) => candidate.errorPathId === row.fromErrorPathId)!;
    if (!error.recoveryPathIds.includes(row.recoveryPathId)) throw new Error(`REQUIRED_APPLICATION_TOPOLOGY_UNRESOLVED_ERROR_RECOVERY:${row.recoveryPathId}`);
  }
  for (const row of value.roles) {
    for (const id of row.permissionIds) assertResolved(permissionIds, id, "ROLE_PERMISSION");
    for (const id of row.policyIds) assertResolved(policyIds, id, "ROLE_POLICY");
  }
  for (const row of value.permissions) {
    for (const id of row.roleIds) assertResolved(roleIds, id, "PERMISSION_ROLE");
    for (const id of row.policyIds) assertResolved(policyIds, id, "PERMISSION_POLICY");
  }
  for (const row of value.terminalOutcomeEdges) assertResolved(nodeIds, row.fromNodeId, "OUTCOME_NODE");

  const { topologyHash, ...withoutHash } = value;
  if (hashCanonicalContent(canonicalWithoutHash(withoutHash)) !== topologyHash) {
    throw new Error("REQUIRED_APPLICATION_TOPOLOGY_HASH_MISMATCH");
  }
}

export function requiredApplicationTopologySliceContentRef(
  value: RequiredApplicationTopologySlice,
): ContentRef {
  assertRequiredApplicationTopologySliceIntrinsicIntegrity(value);
  return {
    ref: `cogo-content:required-application-topology-slice:${value.topologyHash}`,
    contentHash: value.topologyHash,
  };
}
